/**
 * notification-channel-matrix.ts — Settings → Notifications → Marketplace:
 * one row per marketplace notification type, three channels (in-app · email ·
 * text).
 *
 * Backend `proinbox.TypePolicy` is the delivery authority; this file only owns
 * labels and copy. Which cells exist (and their presets) are pinned to policy
 * by the shared fixture `frontend/pro/testdata/notification_channel_matrix.json`
 * — asserted by `notification-channel-matrix.test.ts` here AND by
 * `proinbox/policy_test.go` (TestChannelMatrixFixtureMatchesPolicy), so a
 * switch the backend would ignore, or a text the pro could not turn off,
 * fails CI on whichever side drifted.
 *
 * Cells:
 *   - "on" / "off": a toggle, preset on / off. Backed by a
 *     `{channel, category}` notification-pref row.
 *   - "pinned": always on, not a preference (a pinned email, or an email the
 *     producer sends directly). Display only — never written to the wire.
 *   - "none": no control. The channel can never deliver this type. For text
 *     this is structural (backend `SMSEligible=false`), not a default: only
 *     the four money moments below can ever be texted.
 */
import type { NotificationChannel } from "./types";
import type { NotificationCatalogEntry } from "./adapt";

export type ChannelCell = "on" | "off" | "pinned" | "none";

/** The three columns, in display order. */
export const MATRIX_CHANNELS = [
  "in_app",
  "email",
  "sms",
] as const satisfies ReadonlyArray<NotificationChannel>;
export type MatrixChannel = (typeof MATRIX_CHANNELS)[number];

export const MATRIX_CHANNEL_LABELS: Record<MatrixChannel, string> = {
  in_app: "In-app",
  email: "Email",
  sms: "Text",
};

export interface MarketplaceChannelRow {
  /** Backend proinbox NotificationType. */
  type: string;
  /** Backend TypePolicy.PrefCategory — one category names all three cells. */
  category: string;
  label: string;
  /** Channel-neutral: the row's switches decide HOW, this says WHEN. */
  description: string;
  in_app: ChannelCell;
  email: ChannelCell;
  sms: ChannelCell;
  /** Required for every "pinned" cell — rendered as the switch's description. */
  pinnedReason?: string;
}

export const MARKETPLACE_CHANNEL_MATRIX: ReadonlyArray<MarketplaceChannelRow> = [
  {
    type: "marketplace_request",
    category: "notif_marketplace_request",
    label: "New buyer requests",
    description: "When a buyer asks to connect.",
    in_app: "on",
    email: "on",
    sms: "on",
  },
  {
    type: "marketplace_request_expiring",
    category: "notif_marketplace_request_expiring",
    label: "Expired requests",
    description: "When a buyer's request closes before you confirm it.",
    in_app: "on",
    email: "on",
    sms: "on",
  },
  {
    type: "marketplace_payment_failed",
    category: "notif_marketplace_payment_failed",
    label: "Payment issues",
    description: "When a connection fee needs attention.",
    in_app: "on",
    email: "pinned",
    sms: "on",
    pinnedReason:
      "Payment issue emails stay on so you can restore marketplace access quickly.",
  },
  {
    type: "marketplace_connected",
    category: "notif_marketplace_connected",
    label: "Buyer connected",
    description: "When a buyer becomes a connected client.",
    in_app: "on",
    email: "on",
    sms: "on",
  },
  {
    type: "marketplace_first_recourse",
    category: "notif_marketplace_first_recourse",
    label: "Chance to counter",
    description:
      "When a client of yours is weighing another agent's offer and you can respond.",
    in_app: "on",
    email: "on",
    sms: "none",
  },
  {
    type: "marketplace_connection_ended",
    category: "notif_marketplace_connection_ended",
    label: "Connection ended",
    description: "When a marketplace connection ends.",
    in_app: "on",
    email: "on",
    sms: "none",
  },
  {
    type: "marketplace_client_disconnected",
    category: "notif_marketplace_client_disconnected",
    label: "Client disconnected",
    description: "When a client disconnects from your book.",
    in_app: "on",
    email: "on",
    sms: "none",
  },
  {
    type: "marketplace_refund_receipt",
    category: "notif_marketplace_refund_receipt",
    label: "Refund receipts",
    description: "When a marketplace fee is refunded.",
    in_app: "on",
    email: "on",
    sms: "none",
  },
  {
    type: "marketplace_offer_expired",
    category: "notif_marketplace_offer_expired",
    label: "Offer expired",
    description: "When one of your marketplace offers expires.",
    in_app: "on",
    email: "off",
    sms: "none",
  },
  {
    type: "marketplace_buyer_changed",
    category: "notif_marketplace_buyer_changed",
    label: "Buyer updates",
    description: "When a buyer you made an offer to changes their details.",
    in_app: "on",
    email: "off",
    sms: "none",
  },
  {
    type: "marketplace_verification_decision",
    category: "notif_marketplace_verification_decision",
    label: "Verification decisions",
    description: "When we finish reviewing your license.",
    in_app: "on",
    email: "pinned",
    sms: "none",
    pinnedReason: "Verification decisions are always emailed.",
  },
  {
    type: "safety_review_complete",
    category: "notif_safety_review_complete",
    label: "Safety reviews",
    description: "When a review of a reported conversation is complete.",
    in_app: "on",
    email: "pinned",
    sms: "none",
    pinnedReason: "Review outcomes are always emailed.",
  },
];

/**
 * The toggle cells as notification-catalog entries — the rows Settings reads
 * and writes through `/api/notification-prefs`. Pinned and "none" cells have
 * no wire row.
 */
export function marketplaceCatalogEntries(): NotificationCatalogEntry[] {
  const entries: NotificationCatalogEntry[] = [];
  for (const row of MARKETPLACE_CHANNEL_MATRIX) {
    for (const channel of MATRIX_CHANNELS) {
      const cell = row[channel];
      if (cell !== "on" && cell !== "off") continue;
      entries.push({
        category: row.category,
        label: row.label,
        description: row.description,
        channel,
        onFrequency: "realtime",
        defaultOn: cell === "on",
        section: "marketplace",
      });
    }
  }
  return entries;
}
